import 'intl-tel-input/build/css/intlTelInput.css'

import intlTelInput from 'intl-tel-input'
import { nextTick, ref, watch } from 'vue'

const UTILS_SCRIPT = 'https://cdn.jsdelivr.net/npm/intl-tel-input@19.5.6/build/js/utils.js'

// One intl-tel-input lifecycle for a modal phone field: the widget mounts
// when `open` turns true and is torn down (with the value cleared) when it
// turns false. Bind `container` with a template ref on the element wrapping
// the <InputField type="tel">. `payload()` is the shape the Rails phone
// endpoints take: national digits plus the dial code.
export function usePhoneInput(open) {
  const phone = ref('')
  const valid = ref(false)
  const container = ref(null)
  let iti = null

  // Empties the field without unmounting the widget, for "add another".
  // `valid` is forced off here: the phone watcher asks the widget, which
  // reads the DOM input before the cleared value has been rendered into it.
  const clear = () => {
    phone.value = ''
    valid.value = false
  }

  const reset = () => {
    iti?.destroy()
    iti = null
    clear()
  }

  // Immediate so a field that is already open when its component mounts
  // still gets the widget.
  watch(
    open,
    async isOpen => {
      reset()
      if (!isOpen) return
      await nextTick()
      const input = container.value?.querySelector('input')
      if (!input) return
      iti = intlTelInput(input, { utilsScript: UTILS_SCRIPT, initialCountry: 'us', countrySearch: false })
    },
    { immediate: true }
  )

  // The widget validates what is in the DOM input, so wait for the render
  // that writes the new value there (matters when the value is cleared).
  watch(
    phone,
    () => {
      valid.value = iti?.isValidNumber() ?? false
    },
    { flush: 'post' }
  )

  const payload = () => {
    const dialCode = iti.getSelectedCountryData().dialCode
    const fullDigits = iti.getNumber().replace(/\D/g, '')
    const nationalDigits = fullDigits.startsWith(dialCode) ? fullDigits.slice(dialCode.length) : fullDigits
    return { phone: nationalDigits, country_code: dialCode }
  }

  return { phone, valid, container, payload, clear, reset }
}
